import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Grid,
  Divider,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { createAdminAccount } from "../../../../services/Admin/AdminUsersAccountsApi";

const AddAccountDialog = ({ open, onClose, onAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    role: "user",
    phone: "",
    phone2: "",
    phone3: "",
    address: "",
    address2: "",
    address3: "",
  });

  const roles = [
    { value: "user", label: "محاسب" },
    { value: "captain_order", label: "كابتن اوردر " },
    { value: "delivery", label: " ديليفري" },
    { value: "cashier", label: "الكاشير" },
    { value: "kitchen", label: "المطبخ" },
    { value: "manager", label: "مدير" },
    { value: "head_manager", label: "مدير عام" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("الاسم مطلوب");
      return false;
    }

    if (!formData.username.trim()) {
      setError("اسم المستخدم مطلوب");
      return false;
    }

    if (!formData.password.trim()) {
      setError("كلمة المرور مطلوبة");
      return false;
    }

    if (formData.password.length < 6) {
      setError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return false;
    }

    if (!formData.role) {
      setError("الدور مطلوب");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const payload = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role,
      };

      if (formData.phone.trim()) {
        payload.phone = formData.phone.trim();
      }
      if (formData.address.trim()) {
        payload.address = formData.address.trim();
      }

      // إضافة الهواتف والعناوين الإضافية إذا كانت مملوءة
      if (formData.phone2.trim()) {
        payload.phone2 = formData.phone2.trim();
      }
      if (formData.phone3.trim()) {
        payload.phone3 = formData.phone3.trim();
      }
      if (formData.address2.trim()) {
        payload.address2 = formData.address2.trim();
      }
      if (formData.address3.trim()) {
        payload.address3 = formData.address3.trim();
      }

      const response = await createAdminAccount(payload);

      setSuccess(true);

      // إعادة تعيين الحقول
      setFormData({
        name: "",
        username: "",
        password: "",
        role: "user",
        phone: "",
        phone2: "",
        phone3: "",
        address: "",
        address2: "",
        address3: "",
      });

      // إغلاق الديالوج بعد 1.5 ثانية
      setTimeout(() => {
        onAdded && onAdded(response);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("خطأ في إنشاء الحساب:", err);
      setError(err.message || "حدث خطأ في إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError("");
      setSuccess(false);
      setFormData({
        name: "",
        username: "",
        password: "",
        role: "user",
        phone: "",
        phone2: "",
        phone3: "",
        address: "",
        address2: "",
        address3: "",
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "primary.light",
          color: "primary.dark",
        }}
      >
        <PersonAddIcon />
        إضافة حساب جديد
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            تم إنشاء الحساب بنجاح
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* البيانات الأساسية */}
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
            البيانات الأساسية
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="الاسم الكامل"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="مثال: محمد علي"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="اسم المستخدم"
                name="username"
                value={formData.username}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="مثال: admin@123"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="كلمة المرور"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="6 أحرف على الأقل"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel>الدور</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  label="الدور"
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* بيانات الاتصال */}
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            بيانات الاتصال
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="رقم الهاتف الأساسي"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="مثال: 0790000000"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="رقم الهاتف الثاني (اختياري)"
                name="phone2"
                value={formData.phone2}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="مثال: 0781111111"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="رقم الهاتف الثالث (اختياري)"
                name="phone3"
                value={formData.phone3}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                placeholder="مثال: 0772222222"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* العناوين */}
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            العناوين
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="العنوان الأساسي"
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                multiline
                rows={2}
                placeholder="مثال: Baghdad"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="العنوان الثاني (اختياري)"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                multiline
                rows={2}
                placeholder="مثال: عمارة 5"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="العنوان الثالث (اختياري)"
                name="address3"
                value={formData.address3}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                multiline
                rows={2}
                placeholder="مثال: الدور الثاني"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          startIcon={!loading && <PersonAddIcon />}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? "جاري الإنشاء..." : "إنشاء الحساب"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccountDialog;
